/**
 * Loading Component
 *
 * A simple loading indicator used during authentication initialization
 * and other async operations.
 */

import heroBg1 from '../../../common/assets/images/hero-construction-1.jpg';
import heroBg4 from '../../../common/assets/images/hero-construction-4.jpg';

/**
 * Get background image URL based on current time
 * 9:00-16:00 (business hours): hero-construction-1.jpg
 * Outside business hours: hero-construction-4.jpg
 */
function getBackgroundImage(): string {
  const currentHour = new Date().getHours();
  const isDayTime = currentHour >= 9 && currentHour < 16;
  const imageUrl = isDayTime ? heroBg1 : heroBg4;
  return `url('${imageUrl}')`;
}

interface LoadingProps {
  /** Optional message to display below the spinner */
  message?: string;

  /** Whether to show the component in full-screen mode */
  fullScreen?: boolean;
}

/**
 * Loading Component
 *
 * Displays a spinning loader with an optional message.
 * Can be rendered inline or as a full-screen overlay.
 */
export function Loading({
  message = 'Loading...',
  fullScreen = false,
}: LoadingProps) {
  const containerClasses = fullScreen
    ? 'loading-container loading-fullscreen'
    : 'loading-container';
  const backgroundImage = fullScreen ? getBackgroundImage() : undefined;
  const style = fullScreen ? { backgroundImage } : undefined;

  return (
    <div className={containerClasses} role="status" aria-live="polite" style={style}>
      <div className="loading-spinner" aria-hidden="true">
        <svg
          className="spinner-svg"
          viewBox="0 0 50 50"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            className="spinner-circle"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth="4"
          />
        </svg>
      </div>
      {message && <p className="loading-message">{message}</p>}
    </div>
  );
}

/**
 * PageLoader Component
 *
 * Full-screen loading indicator for page transitions.
 */
export function PageLoader({ message }: { message?: string }) {
  return <Loading fullScreen message={message} />;
}
